'use client';

import * as React from 'react';
import { ThemeProvider as NextThemesProvider } from 'next-themes';

/**
 * A wrapper component for the next-themes ThemeProvider
 *
 * This component provides a unified theme management interface by wrapping
 * the NextThemesProvider from next-themes. It forwards all props to the
 * underlying provider and renders children components within the theme context.
 *
 * @component
 * @param {React.ComponentProps<typeof NextThemesProvider>} props - All props supported by NextThemesProvider
 * @param {React.ReactNode} props.children - Child components to be rendered within the theme context
 *
 * @example
 * ```tsx
 * <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
 *   <App />
 * </ThemeProvider>
 * ```
 *
 * @example
 * ```tsx
 * <ThemeProvider
 *   attribute="data-theme"
 *   defaultTheme="dark"
 *   themes={['light', 'dark', 'system']}
 * >
 *   <MainLayout />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({
  children,
  ...props
}: React.ComponentProps<typeof NextThemesProvider>) {
  return <NextThemesProvider {...props}>{children}</NextThemesProvider>;
}
